import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Filter, Loader2, RefreshCw, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import OperationsLayout, { PageHeader, SeverityBadge, StatusBadge } from './OperationsLayout';
import { listIncidents, createIncident } from '@/lib/operationsApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const SEVERITY_OPTIONS = ["", "critical", "high", "medium", "low"];
const STATUS_OPTIONS   = ["", "open", "investigating", "closed"];

export default function IncidentsPage() {
  const navigate = useNavigate();
  const [incidents, setIncidents] = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState("");
  const [severity, setSeverity]   = useState("");
  const [status, setStatus]       = useState("");
  const [creating, setCreating]   = useState(false);
  const [showForm, setShowForm]   = useState(false);
  const [form, setForm]           = useState({ title: "", alarm_description: "", vendor: "", technology: "", network_function: "", severity: "medium" });

  const load = useCallback(() => {
    setLoading(true);
    const params = { limit: 50 };
    if (severity) params.severity = severity;
    if (status)   params.status   = status;
    listIncidents(params)
      .then(r => { setIncidents(r.items || []); setTotal(r.total || 0); })
      .catch(e => toast.error("Failed to load incidents: " + e.message))
      .finally(() => setLoading(false));
  }, [severity, status]);

  useEffect(() => { load(); }, [load]);

  const filtered = search.trim()
    ? incidents.filter(i =>
        i.title.toLowerCase().includes(search.toLowerCase()) ||
        (i.vendor || "").toLowerCase().includes(search.toLowerCase()) ||
        (i.alarm_id || "").toLowerCase().includes(search.toLowerCase())
      )
    : incidents;

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Title is required");
    setCreating(true);
    try {
      const r = await createIncident(form);
      toast.success("Incident created");
      setShowForm(false);
      setForm({ title: "", alarm_description: "", vendor: "", technology: "", network_function: "", severity: "medium" });
      navigate(`/operations/incidents/${r.incident.id}`);
    } catch (e) {
      toast.error("Failed to create: " + e.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <OperationsLayout>
      <PageHeader
        title="Incidents"
        subtitle={`${total} total incidents`}
        actions={
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={load} disabled={loading}>
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
            <Button size="sm" onClick={() => setShowForm(true)}>
              <Plus className="w-3.5 h-3.5 mr-1" /> New Incident
            </Button>
          </div>
        }
      />

      <div className="p-6 space-y-4">
        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-48">
            <Search className="absolute left-2.5 top-2 w-4 h-4 text-muted-foreground" />
            <Input
              className="pl-8 h-8 text-sm"
              placeholder="Search title, vendor, alarm…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <select
            className="h-8 text-sm border border-input rounded-md px-2 bg-background text-foreground"
            value={status}
            onChange={e => setStatus(e.target.value)}
          >
            <option value="">All status</option>
            {STATUS_OPTIONS.slice(1).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select
            className="h-8 text-sm border border-input rounded-md px-2 bg-background text-foreground"
            value={severity}
            onChange={e => setSeverity(e.target.value)}
          >
            <option value="">All severity</option>
            {SEVERITY_OPTIONS.slice(1).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {/* New incident form */}
        {showForm && (
          <div className="bg-card border border-border rounded-lg p-4 space-y-3">
            <h3 className="text-sm font-semibold">New Incident</h3>
            <form onSubmit={handleCreate} className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <label className="text-xs text-muted-foreground mb-1 block">Title *</label>
                  <Input
                    className="h-8 text-sm"
                    placeholder="Brief description of the incident"
                    value={form.title}
                    onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs text-muted-foreground mb-1 block">Alarm Description</label>
                  <Input
                    className="h-8 text-sm"
                    placeholder="Alarm text or ID"
                    value={form.alarm_description}
                    onChange={e => setForm(f => ({ ...f, alarm_description: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Vendor</label>
                  <Input className="h-8 text-sm" placeholder="e.g. Ericsson" value={form.vendor} onChange={e => setForm(f => ({ ...f, vendor: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Technology</label>
                  <Input className="h-8 text-sm" placeholder="e.g. 5G NR" value={form.technology} onChange={e => setForm(f => ({ ...f, technology: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Network Function</label>
                  <Input className="h-8 text-sm" placeholder="e.g. gNB, AMF" value={form.network_function} onChange={e => setForm(f => ({ ...f, network_function: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Severity</label>
                  <select
                    className="w-full h-8 text-sm border border-input rounded-md px-2 bg-background text-foreground"
                    value={form.severity}
                    onChange={e => setForm(f => ({ ...f, severity: e.target.value }))}
                  >
                    {SEVERITY_OPTIONS.slice(1).map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" size="sm" onClick={() => setShowForm(false)}>Cancel</Button>
                <Button type="submit" size="sm" disabled={creating}>
                  {creating && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
                  Create & Open
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Incidents table */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-2 text-muted-foreground">
              <AlertCircle className="w-8 h-8" />
              <span className="text-sm">No incidents found</span>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Title</th>
                  <th className="text-left px-3 py-2.5 text-xs font-medium text-muted-foreground">Vendor / Tech</th>
                  <th className="text-left px-3 py-2.5 text-xs font-medium text-muted-foreground">Severity</th>
                  <th className="text-left px-3 py-2.5 text-xs font-medium text-muted-foreground">Status</th>
                  <th className="text-left px-3 py-2.5 text-xs font-medium text-muted-foreground">MTTR</th>
                  <th className="text-left px-3 py-2.5 text-xs font-medium text-muted-foreground">Opened</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map(inc => (
                  <tr
                    key={inc.id}
                    onClick={() => navigate(`/operations/incidents/${inc.id}`)}
                    className="hover:bg-accent cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-foreground truncate max-w-xs">{inc.title}</div>
                      {inc.verified && (
                        <span className="text-xs text-green-600 dark:text-green-400">✓ Verified knowledge</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-muted-foreground text-xs">
                      {[inc.vendor, inc.technology].filter(Boolean).join(" · ") || "—"}
                    </td>
                    <td className="px-3 py-2.5"><SeverityBadge severity={inc.severity} /></td>
                    <td className="px-3 py-2.5"><StatusBadge status={inc.status} /></td>
                    <td className="px-3 py-2.5 text-muted-foreground text-xs">
                      {inc.mttr_minutes != null ? `${inc.mttr_minutes}m` : "—"}
                    </td>
                    <td className="px-3 py-2.5 text-muted-foreground text-xs">
                      {inc.created_at ? new Date(inc.created_at).toLocaleDateString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </OperationsLayout>
  );
}
